import {
  forwardRef,
  type HTMLAttributes,
  type KeyboardEvent,
  type ReactElement,
  type ReactNode,
} from "react";

import { cn } from "../utils/cn";

export interface CardProps extends Omit<HTMLAttributes<HTMLDivElement>, "children"> {
  children: ReactNode;
  /** Hover-elevation affordance. Does NOT make the card clickable on its own —
   * pass `onClick` + `interactive` together when the whole card is a button. */
  interactive?: boolean;
  padded?: boolean;
}

export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  { children, interactive = false, padded = true, className, onClick, onKeyDown, role, tabIndex, ...rest },
  ref,
): ReactElement {
  const isClickable = Boolean(onClick);
  // Only treat the card as a focusable widget when the caller opts in
  // by passing `role="button"` (or any other widget role) + tabIndex.
  // Auto-promoting every clickable card to role="button" breaks the
  // WCAG "no nested interactive" rule when the card contains its own
  // buttons/menus. Mouse-click remains a progressive enhancement —
  // keyboard users reach the dedicated buttons inside the card.
  const isWidget = Boolean(role) && tabIndex !== undefined;
  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => {
    onKeyDown?.(e);
    if (!isWidget || !isClickable || e.defaultPrevented) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick?.(e as unknown as React.MouseEvent<HTMLDivElement>);
    }
  };
  return (
    // The div accepts onClick as a mouse-only progressive enhancement
    // (card-anywhere-click). Keyboard-accessible triggers live inside
    // the card (title button / kebab). When callers opt into full
    // button semantics via role="button" + tabIndex, Enter/Space are
    // wired in handleKeyDown above.
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      ref={ref}
      {...(role !== undefined && { role })}
      {...(tabIndex !== undefined && { tabIndex })}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      className={cn(
        "rounded-lg border border-border-default bg-neutral-0 shadow-sm transition-shadow",
        padded && "p-6",
        (interactive || isClickable) &&
          "cursor-pointer hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500",
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
});
